/**
 * @caint/engine — motor de cálculo do IMGQ.
 *
 * Núcleo funcional puro (doc 13.1): mesma entrada → mesma saída, sem IO nem
 * estado global. Importado tanto pelo PWA (cálculo offline ao vivo) quanto pelo
 * backend (revalidação no sync) — fonte ÚNICA do número, garantindo
 * defensabilidade (docs 11/12.3).
 */

import { CONFIG_PADRAO, type ConfigMotor } from "./config.ts";
import { num1, num2, pct } from "./format.ts";
import { contarConf, contarCore, contarNA, imgq, qaciFinal, selo, setorScore, subindice } from "./imgq.ts";
import { Maturidade } from "./maturidade.ts";
import { entraNoPlano, nivelRisco, riscoInerente, riscoResidual } from "./risco.ts";
import type { Eixo, ItemAvaliado, Momento, NivelMaturidade } from "./types.ts";

export * from "./types.ts";
export { type ConfigMotor, CONFIG_PADRAO, type PonderacaoImgq } from "./config.ts";
export { Maturidade, MaturidadeInvalida } from "./maturidade.ts";
export {
  riscoInerente,
  riscoResidual,
  nivelRisco,
  entraNoPlano,
  type EntradaRisco,
} from "./risco.ts";
export {
  setorScore,
  subindice,
  contarCore,
  contarConf,
  contarNA,
  imgq,
  selo,
  qaciFinal,
} from "./imgq.ts";
export { pct, num1, num2 } from "./format.ts";
export {
  itensParaPlano,
  ehCorePeso,
  maturidadeDisparaPlano,
  itensDoEscopo,
  itemNoEscopo,
  type RespostaMaturidade,
  type ItemPlanoGerado,
} from "./planoAcao.ts";

/**
 * Fachada que fixa a config e expõe a API plana usada pela UI portada do
 * protótipo (antes `window.CAINT_ENGINE`). Mantida fina — toda a lógica vive
 * nos módulos puros; aqui só aplicamos a config.
 */
export function createEngine(config: ConfigMotor = CONFIG_PADRAO) {
  const mat = (valor: string) => Maturidade.de(valor);
  return {
    config,
    score: (valor: NivelMaturidade | string | null): number | null | "NA" => {
      if (valor === null || valor === "") {
        return null;
      }
      const m = mat(valor);
      return m.ehNA ? "NA" : m.score(config);
    },
    situacao: (valor: NivelMaturidade | string | null) =>
      valor === null || valor === "" ? "" : mat(valor).situacao,
    conformidade: (valor: NivelMaturidade | string | null) =>
      valor === null || valor === "" ? "" : (mat(valor).conformidade ?? ""),
    riscoInerente: (item: { p: number; i: number }) => riscoInerente(item),
    riscoResidual: (item: ItemAvaliado, momento: Momento) => {
      const m = item[momento];
      const s = m === null || m === undefined ? null : mat(m).score(config);
      return riscoResidual({ p: item.p, i: item.i }, s, config);
    },
    nivelRisco: (valor: number | null) => nivelRisco(valor, config),
    setorScore: (items: readonly ItemAvaliado[], momento: Momento) => setorScore(items, momento, config),
    subindice: (items: readonly ItemAvaliado[], eixo: Eixo, momento: Momento) =>
      subindice(items, eixo, momento, config),
    contarCore: (items: readonly ItemAvaliado[], nivel: 0 | 1, momento: Momento) =>
      contarCore(items, nivel, momento),
    contarConf,
    contarNA,
    imgq: (items: readonly ItemAvaliado[], momento: Momento, qaci?: number, ims?: number) =>
      imgq(items, momento, config, qaci, ims),
    selo: (valor: number | null) => selo(valor, config),
    qaciFinal: (c1: number, c2: number, c3: number) => qaciFinal(c1, c2, c3, config),
    pct,
    num1,
    num2,
  };
}

export type Engine = ReturnType<typeof createEngine>;
